import { RuntimeOperations } from '@helo/cms'
import { If, asyncFlow, flow } from '@helo/utils'
import { StoreApi } from 'zustand'

import { savedViewService } from '../../../services/savedViewService'
import { PersistenceSlice } from '../persistence/persistenceSlice'
import { QuerySlice } from '../queryStore/querySlice'
import { RouterSlice } from '../router/routerSlice'
import { ViewSlice } from './viewSlice'
import {
  DEFAULT_VIEW_STORE_STATE,
  deepMergeViewState,
  getSavedView,
  loadContentViewState,
  persistView,
  pullViewUrlState,
  removeViewQueryState,
  removeViewWidgetState,
  setupBaseViewState,
  setupRuntimeViewState,
  updateIsBaseState,
  updateQueryState,
  updateWidgetState,
} from './viewSlice.parts'
import {
  SavedView,
  ViewQueryState,
  ViewState,
  WidgetState,
} from './viewSlice.types'

const initializeView = () =>
  flow(
    flow.inputExtends<ViewSlice & RouterSlice>(),
    <T extends ViewSlice & RouterSlice>(store: T): T => ({
      ...store,
      viewStore: {
        ...store.viewStore, // keeps methods
        ...DEFAULT_VIEW_STORE_STATE, // resets state
        viewId:
          store.router.routeConfig?.routeId ??
          store.router.pathname.replace('/', ''),
      },
    }),
  )
const loadView = (): (<
  T extends ViewSlice & QuerySlice & PersistenceSlice & RouterSlice,
>(
  store: T,
) => Promise<T>) =>
  asyncFlow(
    asyncFlow.inputExtends<
      ViewSlice & QuerySlice & PersistenceSlice & RouterSlice
    >(),
    loadContentViewState(),
    pullViewUrlState(),
    getSavedView(),
    setupBaseViewState(),
    setupRuntimeViewState(),
    store => {
      /**
       * Must run base view state and runtime viewstate through interpolation.
       *
       * This allows content to define dynamic configurations such as filtering by an id.
       */

      return {
        ...store,
        viewStore: {
          ...store.viewStore,
          baseViewState: RuntimeOperations.processValue()(
            store.viewStore.baseViewState,
          ) as any,
          viewState: RuntimeOperations.processValue()(
            store.viewStore.viewState,
          ) as any,
        },
      } as typeof store
    },
    updateIsBaseState(),
  ) as <T extends ViewSlice & QuerySlice & PersistenceSlice & RouterSlice>(
    store: T,
  ) => Promise<T>

const setWidgetState = (widgetState: WidgetState, persist: boolean = false) =>
  flow(
    flow.inputExtends<ViewSlice & PersistenceSlice>(),
    <T extends ViewSlice>(store: T): T => ({
      ...store,
      viewStore: {
        ...store.viewStore,
        viewState: updateWidgetState(widgetState)(store.viewStore.viewState),
      },
    }),
    updateIsBaseState(),
    If.then(() => persist, persistView()),
  )

/**
 * TODO:
 * -pick widget states so base state can be used too.
 */
const getWidgetState =
  (widgetId: string) =>
  <T extends ViewSlice>(store: T) =>
    store.viewStore.viewState.widgetStates.find(
      widget => widget.widgetId === widgetId,
    )

/**
 * TODO: pick viewstore query states so base state can be used too.
 */
const getQueryState = (queryId: string) => {
  return flow(flow.inputExtends<ViewSlice>(), store =>
    store.viewStore.viewState.queryStates.find(query => {
      return query.queryId === queryId
    }),
  )
}

const getQueryBaseState = (queryId: string) =>
  flow(flow.inputExtends<ViewSlice>(), store =>
    store.viewStore.baseViewState.queryStates.find(
      query => query.queryId === queryId,
    ),
  )

const setWidgetBaseState = (baseState: WidgetState) =>
  flow(
    flow.inputExtends<ViewSlice>(),
    <T extends ViewSlice>(store: T): T => ({
      ...store,
      viewStore: {
        ...store.viewStore,
        baseViewState: updateWidgetState(baseState)(
          store.viewStore.baseViewState,
        ),
      },
    }),
    updateIsBaseState(),
  )

// // TODO: think about persistence and if we can store it in the query state instead.
const setQueryState = (queryState: ViewQueryState, persist: boolean = true) =>
  flow(
    flow.inputExtends<ViewSlice & PersistenceSlice>(),
    store => {
      return {
        ...store,
        viewStore: {
          ...store.viewStore,
          viewState: updateQueryState(queryState)(store.viewStore.viewState),
        },
      }
    },
    updateIsBaseState(),
    If.then(() => persist, persistView()),
  )

const setQueryBaseState = (baseState: ViewQueryState) =>
  flow(
    flow.inputExtends<ViewSlice>(),
    <T extends ViewSlice>(store: T): T => ({
      ...store,
      viewStore: {
        ...store.viewStore,
        baseViewState: updateQueryState(baseState)(
          store.viewStore.baseViewState,
        ),
      },
    }),
    updateIsBaseState(),
  )
const removeQueryState = (queryId: string, persist: boolean = true) =>
  flow(
    flow.inputExtends<ViewSlice & PersistenceSlice>(),
    <T extends ViewSlice>(store: T): T => ({
      ...store,
      viewStore: removeViewQueryState(queryId)(store.viewStore),
    }),
    updateIsBaseState(),
    If.then(() => persist, persistView()),
  )
const removeWidgetState = (widgetId: string, persist: boolean = true) =>
  flow(
    flow.inputExtends<ViewSlice & PersistenceSlice>(),
    <T extends ViewSlice>(store: T): T => ({
      ...store,
      viewStore: removeViewWidgetState(widgetId)(store.viewStore),
    }),
    updateIsBaseState(),
    If.then(() => persist, persistView()),
  )
const createSavedView = (
  savedView: Partial<Pick<SavedView, 'name' | 'description' | 'tableConfig'>>,
  storeApi: StoreApi<ViewSlice & PersistenceSlice & QuerySlice>,
) =>
  asyncFlow(
    asyncFlow.inputExtends<ViewSlice & PersistenceSlice & QuerySlice>(),
    async store => {
      if (!store.viewStore.viewId) {
        throw new Error('View ID is required')
      }

      // The runtime viewState is a partial delta — it only contains properties
      // the user has changed. If the user set sorts but not filters, viewState
      // will have sorts while baseViewState still holds the original filters.
      // A naive save of viewState alone would silently drop the unchanged
      // filters (or sorts). deepMergeViewState overlays the delta onto
      // baseViewState so both survive into the persisted saved view.
      const resolvedViewState = deepMergeViewState(store.viewStore.viewState)(
        store.viewStore.baseViewState,
      ) as ViewState

      const newSavedView = await savedViewService.saveView({
        ...savedView,
        viewId: store.viewStore.viewId,
        viewState: resolvedViewState,
      })
      const currentStore = storeApi.getState()

      return {
        ...currentStore,
        viewStore: {
          ...currentStore.viewStore,
          savedView: newSavedView,
        },
      } as typeof store
    },
    setupBaseViewState(),
    setupRuntimeViewState(),
    store => {
      /**
       * Must run base view state and runtime viewstate through interpolation.
       *
       * This allows content to define dynamic configurations such as filtering by an id.
       */

      return {
        ...store,
        viewStore: {
          ...store.viewStore,
          baseViewState: RuntimeOperations.processValue()(
            store.viewStore.baseViewState,
          ) as any,
          viewState: RuntimeOperations.processValue()(
            store.viewStore.viewState,
          ) as any,
        },
      }
    },
    updateIsBaseState(),
    persistView(),
  )
const updateSavedView = (
  savedView: Partial<Pick<SavedView, 'name' | 'description' | 'tableConfig'>>,
  storeApi: StoreApi<ViewSlice & PersistenceSlice & QuerySlice>,
) =>
  asyncFlow(
    asyncFlow.inputExtends<ViewSlice & PersistenceSlice & QuerySlice>(),
    async store => {
      if (!store.viewStore.savedView?.id) {
        throw new Error('Saved view ID is required')
      }

      // Same rationale as createSavedView: viewState is a partial delta so
      // we must deep-merge it onto baseViewState to avoid losing filters or
      // sorts the user didn't explicitly touch during this editing session.
      const resolvedViewState = deepMergeViewState(store.viewStore.viewState)(
        store.viewStore.baseViewState,
      ) as ViewState

      const updatedSavedView = await savedViewService.updateView({
        id: store.viewStore.savedView.id,
        name: savedView.name,
        description: savedView.description,
        viewState: resolvedViewState,
        tableConfig: savedView.tableConfig,
      })

      const currentStore = storeApi.getState()

      return {
        ...currentStore,
        viewStore: {
          ...currentStore.viewStore,
          savedView: updatedSavedView,
        },
      } as typeof store
    },
    setupBaseViewState(),
    setupRuntimeViewState(),
    store => {
      /**
       * Must run base view state and runtime viewstate through interpolation.
       *
       * This allows content to define dynamic configurations such as filtering by an id.
       */
      return {
        ...store,
        viewStore: {
          ...store.viewStore,
          baseViewState: RuntimeOperations.processValue()(
            store.viewStore.baseViewState,
          ) as any,
          viewState: RuntimeOperations.processValue()(
            store.viewStore.viewState,
          ) as any,
        },
      }
    },
    updateIsBaseState(),
    persistView(),
  )

export { persistView } from './viewSlice.parts'

export const ViewSliceActions = {
  initializeView,
  loadView,
  setWidgetState,
  getWidgetState,
  getQueryBaseState,
  getQueryState,
  setWidgetBaseState,
  setQueryState,
  setQueryBaseState,
  removeQueryState,
  removeWidgetState,
  createSavedView,
  updateSavedView,
}
